import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, Wallet, Building2 } from "lucide-react";

const DEMO_ACCOUNTS = [
  { email: "budowa2@ndi.pl", label: "Pracownik · S6 Odc. 2", role: "employee", tone: "slate" },
  { email: "budowa3@ndi.pl", label: "Pracownik · S6 Odc. 3", role: "employee", tone: "slate" },
  { email: "kierownik2@ndi.pl", label: "Kierownik · S6 Odc. 2", role: "manager", tone: "blue" },
  { email: "kierownik3@ndi.pl", label: "Kierownik · S6 Odc. 3", role: "manager", tone: "blue" },
];

export default function Login() {
  const { user, login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyDemo, setBusyDemo] = useState(null);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się zalogować");
    } finally {
      setSubmitting(false);
    }
  };

  const demoLoginAs = async (em) => {
    setBusyDemo(em);
    try {
      await demoLogin(em);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się zalogować jako demo");
    } finally {
      setBusyDemo(null);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left visual panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900">
        <img
          src="https://images.pexels.com/photos/33166083/pexels-photo-33166083.png"
          alt="Office-Land"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/85 via-slate-900/50 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#FF6B1A] flex items-center justify-center text-white text-lg font-bold shadow-lg">OL</div>
            <div>
              <p className="text-lg font-bold tracking-tight">Office-Land</p>
              <p className="text-xs text-slate-300">Portal zakupowy</p>
            </div>
          </div>

          <div>
            <p className="text-[#FF6B1A] font-semibold text-sm uppercase tracking-widest mb-3">NDI · Portal zakupowy</p>
            <h1 className="text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
              Zakupy dla budów NDI<br />pod kontrolą Office-Land.
            </h1>
            <p className="mt-4 text-slate-300 max-w-md">
              Każda budowa ma swój katalog, budżet i ścieżkę akceptacji.
              Pracownicy zamawiają, kierownicy akceptują, Office-Land realizuje.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 max-w-md">
            <div className="rounded-md bg-white/5 backdrop-blur-sm border border-white/10 p-3">
              <Building2 className="w-4 h-4 text-[#FF6B1A] mb-1.5" />
              <p className="text-xs font-semibold">Wiele budów</p>
            </div>
            <div className="rounded-md bg-white/5 backdrop-blur-sm border border-white/10 p-3">
              <Wallet className="w-4 h-4 text-[#FF6B1A] mb-1.5" />
              <p className="text-xs font-semibold">Budżet pod kontrolą</p>
            </div>
            <div className="rounded-md bg-white/5 backdrop-blur-sm border border-white/10 p-3">
              <ShieldCheck className="w-4 h-4 text-[#FF6B1A] mb-1.5" />
              <p className="text-xs font-semibold">Akceptacje</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[#FF6B1A] flex items-center justify-center text-white font-bold">OL</div>
            <p className="font-bold text-slate-900">Office-Land · NDI</p>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-slate-900">Zaloguj się</h2>
          <p className="mt-1 text-sm text-slate-500">Podaj dane służbowe lub skorzystaj z konta demo.</p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="email@ndi.pl"
                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Hasło</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                placeholder="••••••••"
                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] disabled:opacity-60 text-white font-semibold text-sm transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Zaloguj się
            </button>
          </form>

          <div className="my-8 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase font-semibold tracking-widest text-slate-400">Konta demo</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="space-y-2">
            {DEMO_ACCOUNTS.map((acc) => (
              <button
                key={acc.email}
                onClick={() => demoLoginAs(acc.email)}
                disabled={busyDemo === acc.email}
                data-testid={`demo-login-${acc.role}-${acc.email.split("@")[0]}`}
                className={`group w-full text-left px-4 py-3 rounded-md border transition-all flex items-center justify-between disabled:opacity-60 ${
                  acc.tone === "orange"
                    ? "border-[#FF6B1A]/40 bg-[#FFF4ED] hover:bg-[#FFE6D3] hover:border-[#FF6B1A]"
                    : acc.tone === "blue"
                      ? "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50"
                      : "border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300"
                }`}
              >
                <div>
                  <p className="text-sm font-semibold text-slate-900">{acc.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{acc.email}</p>
                </div>
                {busyDemo === acc.email ? (
                  <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                ) : (
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#FF6B1A] transition-colors" />
                )}
              </button>
            ))}
          </div>

          <p className="mt-8 text-xs text-slate-400 text-center">
            Hasło demo dla wszystkich kont: <span className="font-mono text-slate-600">Demo123!</span>
          </p>
        </div>
      </div>
    </div>
  );
}
